// Everything the contract screens share, in one place. Ops uploads one
// contract PDF per brand and sends it to the brand's publishers — all at once
// or one by one. Each brand + publisher pair gets its own contract, signed in
// order: Ops (on send), then the brand, then the publisher.

/**
 * The denormalised `contract_status` on an Account or HolderAccess row, which
 * is what payout eligibility reads. A publisher stays `acknowledged` once they
 * have signed any version, so a newer pending version does not stop payouts.
 */
export const CONTRACT_STATUS = {
    NOT_SENT    : "not_sent",
    AWAITING    : "awaiting_acknowledgement",
    ACKNOWLEDGED: "acknowledged"
};

export const CONTRACT_STATUS_LABEL = {
    [CONTRACT_STATUS.NOT_SENT]    : "Not sent",
    [CONTRACT_STATUS.AWAITING]    : "Awaiting acknowledgement",
    [CONTRACT_STATUS.ACKNOWLEDGED]: "Acknowledged"
};

export const CONTRACT_STATUS_TONE = {
    [CONTRACT_STATUS.NOT_SENT]    : "neutral",
    [CONTRACT_STATUS.AWAITING]    : "warning",
    [CONTRACT_STATUS.ACKNOWLEDGED]: "success"
};

export const CONTRACT_STATUS_OPTIONS = [
    { value: CONTRACT_STATUS.NOT_SENT, label: CONTRACT_STATUS_LABEL[CONTRACT_STATUS.NOT_SENT] },
    { value: CONTRACT_STATUS.AWAITING, label: CONTRACT_STATUS_LABEL[CONTRACT_STATUS.AWAITING] },
    { value: CONTRACT_STATUS.ACKNOWLEDGED, label: CONTRACT_STATUS_LABEL[CONTRACT_STATUS.ACKNOWLEDGED] }
];

/**
 * One brand + publisher contract's `status`. The two `*_queued` states are a
 * background job in flight — the row moves on by itself within seconds, and
 * the action that queued it stays disabled until it does.
 */
export const PUBLISHER_CONTRACT_STATUS = {
    NOT_SENT          : "not_sent",
    QUEUED            : "queued",
    AWAITING_BRAND    : "awaiting_brand",
    BRAND_SIGN_QUEUED : "brand_sign_queued",
    AWAITING_PUBLISHER: "awaiting_publisher",
    SIGNED            : "signed"
};

export const PUBLISHER_CONTRACT_STATUS_LABEL = {
    [PUBLISHER_CONTRACT_STATUS.NOT_SENT]          : "Not sent",
    [PUBLISHER_CONTRACT_STATUS.QUEUED]            : "Sending…",
    [PUBLISHER_CONTRACT_STATUS.AWAITING_BRAND]    : "Waiting for brand",
    [PUBLISHER_CONTRACT_STATUS.BRAND_SIGN_QUEUED] : "Brand signing…",
    [PUBLISHER_CONTRACT_STATUS.AWAITING_PUBLISHER]: "Waiting for publisher",
    [PUBLISHER_CONTRACT_STATUS.SIGNED]            : "Signed"
};

export const PUBLISHER_CONTRACT_STATUS_TONE = {
    [PUBLISHER_CONTRACT_STATUS.NOT_SENT]          : "neutral",
    [PUBLISHER_CONTRACT_STATUS.QUEUED]            : "neutral",
    [PUBLISHER_CONTRACT_STATUS.AWAITING_BRAND]    : "info",
    [PUBLISHER_CONTRACT_STATUS.BRAND_SIGN_QUEUED] : "info",
    [PUBLISHER_CONTRACT_STATUS.AWAITING_PUBLISHER]: "warning",
    [PUBLISHER_CONTRACT_STATUS.SIGNED]            : "success"
};

// States a background job is still working on; the lists poll while any row
// is in one.
export const PENDING_CONTRACT_STATUSES = [
    PUBLISHER_CONTRACT_STATUS.QUEUED,
    PUBLISHER_CONTRACT_STATUS.BRAND_SIGN_QUEUED
];

export const CONTRACT_POLL_MS = 5000;

// The per-row View action. Once the publisher has signed, their copy is the
// final contract with all three parties filled in.
export const PUBLISHER_VIEW_LABEL = {
    signed : "View signed contract",
    default: "View"
};

// The three parties, in signing order, keyed as the API's `signatures` block
// keys them.
export const SIGNATURE_PARTIES = [
    { key: "ops", label: "Ops" },
    { key: "brand", label: "Brand" },
    { key: "publisher", label: "Publisher" }
];

// The placeholders the brand contract PDF must carry as selectable text. The
// server fills each one in as that party signs, and rejects a PDF missing any
// of them with a 400 that names them.
export const REQUIRED_PLACEHOLDERS = ["{{ops_name}}", "{{brand_name}}", "{{publisher_name}}"];

// The `type` query param on the shared uploader, which picks the storage
// folder the file lands in.
export const CONTRACT_UPLOAD_TYPE = "brand_contract";

export const CONTRACT_MAX_SIZE_MB = 10;

// The server fills each party into the PDF's placeholders, so it only accepts
// a PDF. The extension is checked alongside the MIME type because some uploads
// arrive with an empty `file.type`.
export const CONTRACT_MIME_TYPE = "application/pdf";

export const CONTRACT_EXTENSION = ".pdf";

export const CONTRACT_ACCEPT_ATTR = `${CONTRACT_MIME_TYPE},${CONTRACT_EXTENSION}`;

// How a document is rendered. PDFs are framed in the browser's own reader;
// anything else the /document route serves falls back to the download path.
export const PREVIEW_KIND = {
    PDF : "pdf",
    DOC : "doc",
    NONE: "none"
};

// How the contract is being shown right now. FRAME is the browser's own PDF
// reader in an iframe, which is the only in-page reader the panel ships. The
// document is fetched from the API's /document route and framed as a blob URL,
// since that route needs the bearer token.
export const VIEWER_STATUS = {
    IDLE : "idle",
    FRAME: "frame",
    ERROR: "error"
};

// What the viewer says when the frame cannot show the file: the document
// request failed, the browser has its own PDF reader turned off, or the frame
// never loaded — which is why the download is always offered alongside.
export const VIEWER_ERROR = {
    title  : "This contract could not be displayed here",
    message: "The document could not be loaded in the browser. Open it to read it in full, then confirm below."
};

// How long a frame gets to display the file before the view gives up and
// offers the download instead. A frame that never loads would otherwise sit
// there blank, which is the one failure a contract flow cannot afford to show
// silently.
export const FRAME_TIMEOUT_MS = 15000;

// Shown when a /document request fails without a message of its own.
export const DOCUMENT_ERROR = "Could not open the contract. Please try again.";

// How long a blob URL handed to a new tab or a download outlives the click.
// The tab has loaded it well before then; revoking it frees the bytes.
export const OBJECT_URL_TTL_MS = 60000;

export const THUMBNAIL_STATUS = {
    IDLE   : "idle",
    LOADING: "loading",
    READY  : "ready",
    ERROR  : "error"
};

export const THUMBNAIL_TIMEOUT_MS = 15000;

// Shown beside the frame, which we cannot see inside: if it came up blank,
// this is the way out. Split so the second half can render as the actual link
// that opens the contract.
export const FRAME_NOTE = "Trouble reading the contract here?";

export const FRAME_NOTE_LINK = "Open it in a new tab.";

// E-SIGN/UETA style retention: whoever acknowledges must always be able to
// keep their own copy of what they acknowledged, so Download is never gated.
export const RETENTION_NOTE = "Download a copy to keep for your records.";

// The exact wording the recipient agrees to. Changing this text is changing
// the terms of the acknowledgement, so treat it as versioned content.
export const ACKNOWLEDGEMENT_LABEL = "I have read this contract in full and accept its terms.";

// Why the acknowledgement is still locked. A clickwrap acknowledgement is only
// worth recording if the document was actually put in front of the recipient.
export const REVIEW_HINT = {
    LOADING         : "Loading the contract…",
    DOWNLOAD        : "Download the contract to read it, then confirm below.",
    BROKEN          : "This contract has no readable file. Ask ops to upload it again.",
    // A publisher can only sign once the brand has; the server answers 409
    // until then.
    WAITING_ON_BRAND: "Waiting for the brand to sign this contract. You can review it now and sign once they have."
};

// Shown once the contract has already been acknowledged, so the modal still
// explains what the document is when it is reopened.
export const ACKNOWLEDGED_NOTE = "You have signed this contract. It stays available here for your records.";

// ─── Ops: the save step ────────────────────────────────────────────────────
// The file is already in storage by the time the dialog is shown — what is
// being confirmed is saving it as the brand's contract. Nobody receives it
// until ops sends it to publishers.
export const CONFIRM_TITLE = "Save this contract?";

export const CONFIRM_NOTE = "This becomes the brand's contract. No publisher receives it until you send it to them.";

// Only shown when one already exists: the new version is what gets sent from
// now on. Publishers who signed an earlier version stay payout-eligible.
export const CONFIRM_REPLACE_NOTE = "This saves a new version of the contract. Send it to publishers when you are ready — anyone who signed the previous version keeps receiving payouts meanwhile.";

export const CONFIRM_PREVIEW_ERROR = "This file could not be previewed here. Open the uploaded copy to check it before saving.";
